// branchAdapter.ts
//
// Compatibility layer between the new `zapee_branches` collection (nested
// schema: location.*, address.*, deliveryConfig.*, banners[], platformFee …)
// and the flat BranchModel the app's screens already use.
//
//   flattenBranch()  — nested Firestore doc → flat object (for reads)
//   toNestedBranch() — flat form values     → nested payload (for writes)
//
// Charge fields the old screens edit (delivery/packing/tax/service/kmcharge/
// minAmount/range) have no home in the new nested schema, so they are kept as
// top-level keys. Writes use setDoc({merge:true}) so untouched nested
// sub-fields (location.geohash, deliveryConfig, weather*, …) are preserved.

export const BRANCH_COLLECTION = 'zapee_branches';

const num = (v: any, d = 0): number => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return isNaN(n) ? d : n;
};

/** Nested `zapee_branches` doc → flat shape the screens read. */
export function flattenBranch(docId: string, data: any): any {
  const loc = data.location || {};
  const addr = data.address || {};

  return {
    uid: docId,
    branchId: data.branchId || docId,
    storeuid: data.storeuid || data.branchId || docId,
    storename: data.name || data.storename || '',
    storecate: data.storecate || '',
    email: data.email || '',
    phone: data.phone || '',
    online: data.online ?? true,
    active: data.active ?? true,
    announcement: data.announcement || '',
    policy: data.policy || '',

    pincode: addr.pincode ?? loc.pincode ?? data.pincode ?? '',
    storeLat: String(loc.latitude ?? data.storeLat ?? ''),
    storeLon: String(loc.longitude ?? data.storeLon ?? ''),

    // media
    image: data.image || data.logo || '',
    bannerImages: data.banners || data.bannerImages || [],
    store: data.store || [],

    // charges (top-level fallbacks — not part of the new nested schema)
    delivery: num(data.delivery),
    kmcharge: num(data.kmcharge),
    minAmount: num(data.minAmount),
    packing: num(data.packing),
    service: num(data.service ?? data.platformFee),
    tax: num(data.tax),
    range: num(data.range ?? data.radiusKm),
    radius: String(data.radius ?? data.radiusKm ?? ''),

    // new-schema extras preserved for round-tripping
    platformFee: num(data.platformFee),
    surgeAmount: num(data.surgeAmount),

    timestamp: data.createdAt ?? data.timestamp,
  };
}

/** Map an array of Firestore snapshots to flat branch objects. */
export function flattenBranchDocs(docs: { id: string; data: () => any }[]): any[] {
  return docs.map(d => flattenBranch(d.id, d.data()));
}

/**
 * Flat form values → nested payload for the new collection.
 * Use with setDoc(ref, payload, { merge: true }) to preserve untouched
 * nested sub-fields. Pass `isNew` to seed defaults for brand-new docs.
 */
export function toNestedBranch(flat: any, isNew = false): any {
  const payload: any = {
    name: flat.storename ?? '',
    storename: flat.storename ?? '',
    branchId: flat.branchId ?? '',
    storeuid: flat.storeuid ?? flat.branchId ?? '',
    storecate: flat.storecate ?? '',
    email: flat.email ?? '',
    phone: flat.phone ?? '',
    online: flat.online ?? true,
    active: flat.active ?? true,
    announcement: flat.announcement ?? '',
    policy: flat.policy ?? '',

    address: { pincode: flat.pincode ?? '' },
    location: { latitude: num(flat.storeLat), longitude: num(flat.storeLon) },

    image: flat.image || '',
    banners: Array.isArray(flat.bannerImages) ? flat.bannerImages : [],
    bannerImages: Array.isArray(flat.bannerImages) ? flat.bannerImages : [],
    store: Array.isArray(flat.store) ? flat.store : [],

    // charges kept top-level (schemaless)
    delivery: num(flat.delivery),
    kmcharge: num(flat.kmcharge),
    minAmount: num(flat.minAmount),
    packing: num(flat.packing),
    service: num(flat.service),
    tax: num(flat.tax),
    range: num(flat.range),
    radius: flat.radius ?? '',

    platformFee: num(flat.platformFee ?? flat.service),
    surgeAmount: num(flat.surgeAmount),
    radiusKm: num(flat.range ?? flat.radius),
  };

  if (isNew) {
    payload.bannersEnabled = false;
    payload.deliveryConfig = { minDeliveryTime: 30, maxDeliveryTime: 65 };
  }

  return payload;
}
